import { Button, HeadingComponent, SvgIcon, SvgIconButton, useTheme } from '@salecycle/ui-component-library';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import {
  AlertVariant,
  HeadingOnlyHeading,
  variationToIcon,
  Wrapper,
  BorderLeft,
  Content,
  TitleDescriptionWrapper,
  Description,
  IconBackground,
  HeadingWrapper,
} from './Alert.styles';

export interface AlertProps extends IntrinsicProps {
  /** The id. */
  id?: string;
  /** The heading text. */
  heading: string | ReactNode;
  /** The description of the alert. */
  description?: string | ReactNode;
  /** The body's aria-label. */
  descriptionAriaLabel?: string;
  /** The style variation of the alert. One of "info" (default), "warning", "error", "success". */
  variant?: AlertVariant;
  /** Event fired when the alert is closed. If undefined then a close button will not appear. */
  onClose?: () => void;
  /** The close button's aria-label. */
  closeAriaLabel?: string;
  /** Set the minimum width of the alert. If undefined then the alert will span the full width of it's container.  */
  minWidth?: string;
  /** Set if the alert box has an expandable/collapsible content section */
  collapsible?: boolean;
  /** Set true if the alert box should be default collpased - works with collapsible prop */
  collapsed?: boolean;
  /** The toggle collapse button label when the alert is in collapsed state, default: "Read more" */
  collapseButtonLabelOpen?: string | ReactNode;
  /** The toggle collapse button label when the alert is in expanded state, default: "Minimise" */
  collapseButtonLabelClose?: string | ReactNode;
}

const Heading = ({ heading, headingOnly }) => {
  if (headingOnly) {
    return <HeadingOnlyHeading>{heading}</HeadingOnlyHeading>;
  } else {
    return <HeadingComponent variation="H3">{heading}</HeadingComponent>;
  }
};

export const Alert = ({
  id = 'Alert',
  variant = 'info',
  heading,
  description,
  descriptionAriaLabel,
  onClose,
  closeAriaLabel,
  minWidth,
  collapsible = false,
  collapsed = false,
  collapseButtonLabelOpen = 'Read more',
  collapseButtonLabelClose = 'Minimise',
  ...props
}: AlertProps) => {
  const theme = useTheme();
  const icon = useMemo(() => variationToIcon[variant], [variant]);
  const iconColour = useMemo(() => theme.designToken.baseWhite, [theme.designToken.baseWhite]);
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const isCollapsible = collapsible && !!description;

  const [alertVisible, setAlertVisible] = useState(true);

  const handleOnClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
    setAlertVisible(false);
  }, [onClose]);

  const handleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const getAngleIcon = (collapsed: boolean): 'angle-down' | 'angle-up' => {
    return collapsed ? 'angle-down' : 'angle-up';
  };

  if (!alertVisible) {
    return null;
  }

  return (
    <Wrapper {...props} minWidth={minWidth} headingOnly={!description} role="alert" id={id} data-testid={id}>
      <BorderLeft variant={variant} headingOnly={!description} />
      <Content headingOnly={!description}>
        <HeadingWrapper headingOnly={!description}>
          <IconBackground variant={variant}>
            <SvgIcon name={icon} size="24" color={iconColour} data-testid="Icon" />
          </IconBackground>
          <TitleDescriptionWrapper>
            {typeof heading === 'string' ? <Heading heading={heading} headingOnly={!description} /> : heading}
          </TitleDescriptionWrapper>
          {onClose && !collapsible && (
            <div>
              <SvgIconButton
                onClick={handleOnClose}
                ariaLabel={closeAriaLabel}
                name="xmark"
                size="24"
                data-testid="CloseButton"
              />
            </div>
          )}
          {isCollapsible && (
            <div>
              <Button
                variation="tertiary"
                rightSideIcon={<SvgIcon name={getAngleIcon(isCollapsed)} size="16" />}
                onClick={handleCollapse}
                data-testid="CollapseButton"
              >
                {isCollapsed && collapseButtonLabelOpen}
                {!isCollapsed && collapseButtonLabelClose}
              </Button>
            </div>
          )}
        </HeadingWrapper>
        {description && (!isCollapsed || !isCollapsible) && (
          <Description aria-label={descriptionAriaLabel} collapsible={isCollapsible} data-testid="alertDescription">
            {description}
          </Description>
        )}
      </Content>
    </Wrapper>
  );
};
